import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import {
  TrendingUp as TrendingIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { StatusGlobal } from "src/interface/enum/StatusGlobal";
import { FollowUpType } from "src/interface/enum/FollowUpType";

interface AdvancedStatisticsProps {
  tasks?: any[];
  followups?: any[];
  agreements?: any[];
  transactions?: any[];
  services?: any[];
  isLoading?: boolean;
}

const statusIcons = {
  [StatusGlobal.NotStarted]: <ScheduleIcon />,
  [StatusGlobal.Reported]: <AssessmentIcon />,
  [StatusGlobal.InProgress]: <TrendingIcon />,
  [StatusGlobal.PendingPayment]: <PaymentIcon />,
  [StatusGlobal.Completed]: <CheckIcon />,
  [StatusGlobal.Canceled]: <ErrorIcon />,
  [StatusGlobal.Completed2]: <CheckIcon />,
};

const statusColors = {
  [StatusGlobal.NotStarted]: "default",
  [StatusGlobal.Reported]: "info",
  [StatusGlobal.InProgress]: "warning",
  [StatusGlobal.PendingPayment]: "secondary",
  [StatusGlobal.Completed]: "success",
  [StatusGlobal.Canceled]: "error",
  [StatusGlobal.Completed2]: "success",
};

const statusLabels = {
  [StatusGlobal.NotStarted]: "انجام نشده",
  [StatusGlobal.Reported]: "گزارش داده",
  [StatusGlobal.InProgress]: "در حال انجام",
  [StatusGlobal.PendingPayment]: "در انتظار پرداخت",
  [StatusGlobal.Completed]: "انجام شده",
  [StatusGlobal.Canceled]: "لغو شده",
  [StatusGlobal.Completed2]: "انجام شده(دورکار)",
};

export default function AdvancedStatistics({
  tasks = [],
  followups = [],
  agreements = [],
  transactions = [],
  services = [],
  isLoading = false,
}: AdvancedStatisticsProps) {
  // محاسبه آمار پیشرفته
  const advancedStats = useMemo(() => {
    // اطمینان از اینکه همه داده‌ها آرایه هستند
    const safeTasks = Array.isArray(tasks) ? tasks : [];
    const safeFollowups = Array.isArray(followups) ? followups : [];
    const safeAgreements = Array.isArray(agreements) ? agreements : [];
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    const safeServices = Array.isArray(services) ? services : [];

    // آمار کارها بر اساس وضعیت
    const taskStatusBreakdown = safeTasks.reduce(
      (acc, task) => {
        const status = task.statusTask || StatusGlobal.NotStarted;
        if (!acc[status]) {
          acc[status] = {
            count: 0,
            tasks: [],
          };
        }
        acc[status].count++;
        acc[status].tasks.push(task);
        return acc;
      },
      {} as Record<StatusGlobal, { count: number; tasks: any[] }>
    );

    // آمار پیگیری‌ها بر اساس نوع
    const followupTypeBreakdown = safeFollowups.reduce(
      (acc, followup) => {
        const type = followup.followUpType || FollowUpType.Monthly;
        if (!acc[type]) {
          acc[type] = {
            count: 0,
            followups: [],
          };
        }
        acc[type].count++;
        acc[type].followups.push(followup);
        return acc;
      },
      {} as Record<FollowUpType, { count: number; followups: any[] }>
    );

    // محاسبه درصد تکمیل
    const completedTasks = safeTasks.filter(
      (t) => t.statusTask === StatusGlobal.Completed
    ).length;
    const completionRate =
      safeTasks.length > 0 ? (completedTasks / safeTasks.length) * 100 : 0;

    // محاسبه کارهای فوری (تا 7 روز آینده)
    const urgentTasks = safeTasks.filter((task) => {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      const diffTime = dueDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return (
        diffDays <= 7 &&
        diffDays >= 0 &&
        task.statusTask !== StatusGlobal.Completed
      );
    });

    // محاسبه کارهای تاخیر خورده
    const overdueTasks = safeTasks.filter((task) => {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      return dueDate < today && task.statusTask !== StatusGlobal.Completed;
    });

    // آمار تراکنش‌ها
    const totalRevenue = safeTransactions
      .filter((t) => t.transactionStatus === 1)
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const pendingPayments = safeTransactions
      .filter((t) => t.transactionStatus === 0)
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // آمار خدمات محبوب
    const serviceStats = safeServices.reduce(
      (acc, service) => {
        const agreementCount = safeAgreements.filter((a) =>
          a.services?.some((s: any) => s.serviceRef === service.id)
        ).length;

        if (agreementCount > 0) {
          acc.push({
            service,
            agreementCount,
            revenue:
              safeTransactions
                .filter((t) => t.transactionStatus === 1)
                .reduce((sum, t) => sum + (t.amount || 0), 0) *
              (agreementCount / safeAgreements.length),
          });
        }
        return acc;
      },
      [] as Array<{ service: any; agreementCount: number; revenue: number }>
    );

    // مرتب‌سازی خدمات بر اساس محبوبیت
    serviceStats.sort(
      (
        a: { service: any; agreementCount: number; revenue: number },
        b: { service: any; agreementCount: number; revenue: number }
      ) => b.agreementCount - a.agreementCount
    );

    return {
      taskStatusBreakdown,
      followupTypeBreakdown,
      completionRate,
      urgentTasks,
      overdueTasks,
      totalRevenue,
      pendingPayments,
      serviceStats: serviceStats.slice(0, 5), // 5 خدمت برتر
    };
  }, [tasks, followups, agreements, transactions, services]);

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        آمار پیشرفته
      </Typography>

      {/* کارت‌های آمار کلیدی */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "success.main", mx: "auto", mb: 2 }}>
                <CheckIcon />
              </Avatar>
              <Typography variant="h4" color="success.main">
                {advancedStats.completionRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                نرخ تکمیل کارها
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "warning.main", mx: "auto", mb: 2 }}>
                <WarningIcon />
              </Avatar>
              <Typography variant="h4" color="warning.main">
                {advancedStats.urgentTasks.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                کارهای فوری (7 روز آینده)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "error.main", mx: "auto", mb: 2 }}>
                <ErrorIcon />
              </Avatar>
              <Typography variant="h4" color="error.main">
                {advancedStats.overdueTasks.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                کارهای تاخیر خورده
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "info.main", mx: "auto", mb: 2 }}>
                <PaymentIcon />
              </Avatar>
              <Typography variant="h4" color="info.main">
                {advancedStats.pendingPayments.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                پرداخت‌های معلق (تومان)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* جدول وضعیت کارها */}
        <Grid xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              تفصیل وضعیت کارها
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>وضعیت</TableCell>
                    <TableCell align="center">تعداد</TableCell>
                    <TableCell align="center">درصد</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(advancedStats.taskStatusBreakdown).map(
                    ([status, data]) => {
                      const statusKey = Number(status) as StatusGlobal;
                      const typedData = data as { count: number; tasks: any[] };
                      return (
                        <TableRow key={status}>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {statusIcons[statusKey]}
                              <Chip
                                label={statusLabels[statusKey]}
                                color={statusColors[statusKey] as any}
                                size="small"
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {typedData.count}
                          </TableCell>
                          <TableCell align="center">
                            {tasks.length > 0
                              ? (
                                  (typedData.count / tasks.length) *
                                  100
                                ).toFixed(1)
                              : 0}
                            %
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* لیست کارهای فوری */}
        <Grid xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              کارهای فوری (تا 7 روز آینده)
            </Typography>
            <List dense>
              {advancedStats.urgentTasks.slice(0, 5).map((task, index) => {
                const dueDate = new Date(task.dueDate);
                const today = new Date();
                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                return (
                  <ListItem key={task.id || index}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "warning.main" }}>
                        <ScheduleIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={task.desc || "بدون توضیح"}
                      secondary={`سررسید: ${diffDays} روز دیگر - ${dueDate.toLocaleDateString("fa-IR")}`}
                    />
                    <Chip
                      label={
                        statusLabels[
                          (Number(task.statusTask) ||
                            StatusGlobal.NotStarted) as StatusGlobal
                        ]
                      }
                      color={
                        statusColors[
                          (Number(task.statusTask) ||
                            StatusGlobal.NotStarted) as StatusGlobal
                        ] as any
                      }
                      size="small"
                    />
                  </ListItem>
                );
              })}
              {advancedStats.urgentTasks.length === 0 && (
                <ListItem>
                  <ListItemText primary="هیچ کار فوری وجود ندارد" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* خدمات محبوب */}
        <Grid xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              خدمات محبوب
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>نام خدمت</TableCell>
                    <TableCell align="center">تعداد قرارداد</TableCell>
                    <TableCell align="center">درآمد تخمینی (تومان)</TableCell>
                    <TableCell align="center">درصد از کل</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {advancedStats.serviceStats.map(
                    (
                      item: {
                        service: any;
                        agreementCount: number;
                        revenue: number;
                      },
                      index: number
                    ) => (
                      <TableRow key={item.service.id || index}>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "primary.main",
                                width: 32,
                                height: 32,
                              }}
                            >
                              <BusinessIcon />
                            </Avatar>
                            {item.service.name}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {item.agreementCount}
                        </TableCell>
                        <TableCell align="center">
                          {item.revenue.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          {agreements.length > 0
                            ? (
                                (item.agreementCount / agreements.length) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
